import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { useCart } from '../contexts/CartContext';
import { saveCart, loadCart } from '../services/firestoreService';
import Swal from 'sweetalert2';
import styles from './CustomerLogin.module.css';

/** Import the responsive variants so the bundler copies them */
import bg480Avif  from '../images/login-480.avif';
import bg768Avif  from '../images/login-768.avif';
import bg1200Avif from '../images/login-1200.avif';
import bg1920Avif from '../images/login-1920.avif';

import bg480Webp  from '../images/login-480.webp';
import bg768Webp  from '../images/login-768.webp';
import bg1200Webp from '../images/login-1200.webp';
import bg1920Webp from '../images/login-1920.webp';

import bg480Jpg   from '../images/login-480.jpg';   // also used as blurred placeholder (small)
import bg768Jpg   from '../images/login-768.jpg';
import bg1200Jpg  from '../images/login-1200.jpg';
import bg1920Jpg  from '../images/login-1920.jpg';

const errorMessages = {
  'auth/invalid-email': 'La dirección de correo electrónico no es válida.',
  'auth/user-disabled': 'La cuenta de usuario ha sido deshabilitada.',
  'auth/user-not-found': 'No se encontró ninguna cuenta con este correo electrónico.',
  'auth/wrong-password': 'La contraseña es incorrecta.',
  'auth/invalid-credential': 'Las credenciales no son válidas.',
  'default': 'Se ha producido un error. Por favor, inténtelo de nuevo más tarde.',
};

function BackgroundImage() {
  return (
    <div className={styles.bgWrap} aria-hidden="true">
      {/* Tiny blurred preview: always loads instantly, sits at the back */}
      <img 
        src={bg480Jpg} 
        alt="" 
        className={styles.bgBlur} 
      />

      {/* Responsive sharp image goes above the blur */}
      <picture>
        {/* Best (when supported) */}
        <source
          type="image/avif"
          srcSet={[
            `${bg480Avif} 480w`,
            `${bg768Avif} 768w`,
            `${bg1200Avif} 1200w`,
            `${bg1920Avif} 1920w`,
          ].join(', ')}
          sizes="100vw"
        />

        {/* WebP fallback */}
        <source
          type="image/webp"
          srcSet={[
            `${bg480Webp} 480w`,
            `${bg768Webp} 768w`,
            `${bg1200Webp} 1200w`,
            `${bg1920Webp} 1920w`,
          ].join(', ')}
          sizes="100vw"
        />

        {/* JPEG fallback */}
        <img
          className={styles.bgImg}
          alt=""
          src={bg1920Jpg}
          srcSet={[
            `${bg480Jpg} 480w`,
            `${bg768Jpg} 768w`,
            `${bg1200Jpg} 1200w`,
            `${bg1920Jpg} 1920w`,
          ].join(', ')}
          sizes="100vw"
          loading="eager"           /* loads fast: above-the-fold */
          decoding="async"
          fetchpriority="high"
        />
      </picture>
    </div>
  );
}

const CustomerLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const { setCart } = useCart();
  const auth = getAuth();
  const firestore = getFirestore();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);

      const guestCart = JSON.parse(localStorage.getItem('cart')) || [];
      const userCart = await loadCart();
      const mergedCart = mergeCarts(guestCart, userCart);

      await saveCart(mergedCart);
      setCart(mergedCart);
      localStorage.removeItem('cart');

      const q = query(collection(firestore, 'admins'), where('email', '==', email));
      const querySnapshot = await getDocs(q);
      const isAdmin = !querySnapshot.empty;

      if (isAdmin) {
        Swal.fire('¡Inicio de sesión exitoso!', '¡Bienvenido Admin!', 'success');
        navigate('/admin/management');
      } else {
        Swal.fire('¡Inicio de sesión exitoso!', '¡Bienvenido!', 'success');
        navigate('/products');
      }
    } catch (error) {
      console.error('Error logging in:', error);
      const message = errorMessages[error.code] || errorMessages['default'];
      Swal.fire('Error', message, 'error');
    }
  };

  const mergeCarts = (guestCart, userCart) => {
    const mergedCart = [...userCart];
    guestCart.forEach((guestItem) => {
      const existingItem = mergedCart.find((item) => item.id === guestItem.id);
      if (existingItem) {
        existingItem.quantity = Math.min(
          existingItem.quantity + guestItem.quantity,
          existingItem.stock
        );
      } else {
        mergedCart.push(guestItem);
      }
    });
    return mergedCart;
  };

  return (
    <div className={styles.container}>
      <BackgroundImage />

      <div className={styles.formWrapper}>
        <h2>Login</h2>
        <form onSubmit={handleLogin}>
          <div className={styles.formGroup}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              required
            />
          </div>
          <button type="submit" className={styles.btnPrimary}>Login</button>
        </form>

        <div className={styles.footer}>
          <Link to="/forgot-password">¿Olvidaste la contraseña?</Link>
        </div>
        <div className={styles.footer}>
          <Link to="/customer/register">¿No tienes una cuenta?</Link>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
